import React, { useMemo } from 'react';
import GanttChart, { gunEkle, gunFarki, tariheCevir } from './GanttChart';

/**
 * Enerji süreçlerini Gantt modeline çevirir.
 *
 * Her adım, bir öncekinin GERÇEK bitişinden başlar; bir adım terminini aşarsa
 * aşan kısım kırmızı çizilir ve sonraki adımlar o kadar sağa kayar.
 *
 * `adimBazliSatir` true iken her adım kendi satırında çizilir — tek lokasyonun
 * zaman çizelgesi için kullanılır.
 */
export const enerjiGanttSatirlari = (satirlar = [], tanim, adimBazliSatir = false) => {
  const adimMap = new Map((tanim?.adimlar || []).map((a) => [a.anahtar, a]));
  const bugun = new Date();
  bugun.setHours(0, 0, 0, 0);

  return satirlar.reduce((cikti, satir) => {
    const akis = (satir.secili_adimlar || []).filter((a) => a !== 'enerji_tipi');
    if (!akis.length) return cikti;

    const baslangic =
      tariheCevir(satir.gorev_kabul_tarihi) ||
      tariheCevir(satir.adim_baslangic_tarihi) ||
      tariheCevir(satir.kurulum_tarihi) ||
      bugun;

    let imlec = baslangic;
    const cubuklar = [];

    akis.forEach((anahtar) => {
      const adim = adimMap.get(anahtar);
      const hucre = satir.adim_durumlari?.[anahtar];
      if (!adim || !hucre || hucre.durum === 'kapsam_disi') return;

      const terminGun = adim.termin_gun || 0;
      const planBitis = gunEkle(imlec, terminGun);
      let gercekBitis = planBitis;
      let asimGun = 0;

      let olculdu = false;
      let kazancGun = 0;

      if (hucre.durum === 'tamamlandi') {
        const bitis = tariheCevir(hucre.tarih);
        // 'bitis > imlec' koşulu vardı: imlecin gerisinde kalan (geriye dönük
        // ya da sıra dışı girilmiş) gerçek bitişler sessizce atılıyor ve zincir
        // plana geri dönüyordu. Artık kayıtlı tarih varsa DAİMA kullanılır;
        // imlecin gerisindeyse adım aynı gün bitmiş sayılır (negatif süre yok).
        if (bitis) {
          gercekBitis = bitis > imlec ? bitis : imlec;
          olculdu = true;
          asimGun = Math.max(0, gunFarki(gercekBitis, planBitis));
          kazancGun = Math.max(0, gunFarki(planBitis, gercekBitis));
        }
      } else if (hucre.durum === 'aktif') {
        gercekBitis = bugun > planBitis ? bugun : planBitis;
        asimGun = Math.max(0, gunFarki(bugun, planBitis));
      }

      cubuklar.push({
        ad: adim.ad,
        // Beklemedeki süreçte aktif adım sarı gösterilir.
        durum: satir.beklemede && hucre.durum === 'aktif' ? 'beklemede' : hucre.durum,
        altDurum: hucre.alt_durum,
        baslangic: imlec,
        planBitis,
        gercekBitis,
        asimGun,
        // Erken bitiş: plan bitişi ile gerçek bitiş arasındaki kazanç. Sonraki
        // adımın geri sayımı gercekBitis'ten başladığı için bu kazanç zincire
        // yansır; grafik de bunu görünür kılar.
        kazancGun,
        olculdu,
        terminGun,
      });
      imlec = gercekBitis;
    });

    if (!cubuklar.length) return cikti;

    if (adimBazliSatir) {
      // Tek lokasyon görünümü: her ADIM kendi satırında çizilir; böylece adım
      // adları solda okunur ve zincirleme gecikme açıkça görülür.
      cubuklar.forEach((c, i) => {
        cikti.push({
          anahtar: `${satir.lokasyon}-${c.ad}-${i}`,
          etiket: c.ad,
          cubuklar: [c],
        });
      });
    } else {
      cikti.push({
        anahtar: String(satir.lokasyon),
        etiket: satir.lokasyon_adi || String(satir.lokasyon),
        cubuklar,
      });
    }
    return cikti;
  }, []);
};

/**
 * @param {boolean} adimBazli - true ise her adım ayrı satır (tek lokasyon görünümü)
 */
const EnerjiGantt = ({ satirlar = [], tanim, adimBazli = true }) => {
  const ganttSatirlari = useMemo(
    () => enerjiGanttSatirlari(satirlar, tanim, adimBazli),
    [satirlar, tanim, adimBazli]
  );
  return (
    <GanttChart
      satirlar={ganttSatirlari}
      satirYukseklik={adimBazli ? 30 : 26}
      bosMesaj="Çizilecek enerji süreci yok"
    />
  );
};

export default EnerjiGantt;
